import React, { useState, useEffect, useRef } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { ArrowRight, Play, ChevronDown, ShoppingBag, Menu, X, Mail, Instagram, Youtube, ExternalLink } from 'lucide-react';

// ============================================
// INSPIYAH CULTURE - Cathedral Streetwear
// "We bridge the trust for tomorrow. 
//  Divine Intelligence Illuminates, 
//  Human Intelligence Leads, 
//  AI Intelligence Expands©"
// ============================================

const InspiyahCulture = () => {
  const [email, setEmail] = useState('');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const heroRef = useRef(null);
  
  const { scrollYProgress } = useScroll();
  const heroOpacity = useTransform(scrollYProgress, [0, 0.2], [1, 0]);
  const heroScale = useTransform(scrollYProgress, [0, 0.2], [1, 1.1]);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setIsSubmitting(true);
    // Simulate API call - replace with actual Shopify/Klaviyo integration
    await new Promise(resolve => setTimeout(resolve, 1500));
    setSubmitted(true);
    setIsSubmitting(false);
  };

  // Noise texture overlay
  const NoiseOverlay = () => (
    <div 
      className="fixed inset-0 pointer-events-none z-50 opacity-[0.03]"
      style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
      }}
    />
  );

  return (
    <div className="min-h-screen bg-[#050505] text-white overflow-x-hidden">
      <NoiseOverlay />
      
      {/* ============ NAVIGATION ============ */}
      <nav className="fixed top-0 left-0 right-0 z-40 bg-[#050505]/80 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <a href="#" className="flex items-center space-x-2" data-testid="nav-logo">
              <span className="text-2xl font-bold tracking-tighter" style={{ fontFamily: 'Syne, sans-serif' }}>
                INSPIYAH
              </span>
              <span className="text-[#D4AF37] text-xs tracking-[0.3em] uppercase" style={{ fontFamily: 'Manrope, sans-serif' }}>
                CULTURE
              </span>
            </a>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center space-x-8">
              <a href="#about" className="text-sm text-white/60 hover:text-white transition-colors tracking-wide" style={{ fontFamily: 'Manrope, sans-serif' }}>
                ABOUT
              </a>
              <a href="#collection" className="text-sm text-white/60 hover:text-white transition-colors tracking-wide" style={{ fontFamily: 'Manrope, sans-serif' }}>
                COLLECTION
              </a>
              <a href="#blog" className="text-sm text-white/60 hover:text-white transition-colors tracking-wide" style={{ fontFamily: 'Manrope, sans-serif' }}>
                MIND UNPLUGGED
              </a>
              <a 
                href="#vip" 
                className="px-6 py-2.5 bg-white text-black text-sm font-medium hover:bg-[#D4AF37] transition-all duration-300"
                style={{ fontFamily: 'Manrope, sans-serif' }}
                data-testid="nav-vip-btn"
              >
                JOIN VIP
              </a>
            </div>

            {/* Mobile Menu Toggle */}
            <button 
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-2 text-white"
              data-testid="mobile-menu-toggle"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden bg-[#0A0A0A] border-t border-white/5"
            >
              <div className="px-6 py-8 space-y-6">
                <a href="#about" className="block text-lg text-white/80 hover:text-white" onClick={() => setIsMenuOpen(false)}>About</a>
                <a href="#collection" className="block text-lg text-white/80 hover:text-white" onClick={() => setIsMenuOpen(false)}>Collection</a>
                <a href="#blog" className="block text-lg text-white/80 hover:text-white" onClick={() => setIsMenuOpen(false)}>Mind Unplugged</a>
                <a href="#vip" className="block px-6 py-3 bg-white text-black text-center font-medium" onClick={() => setIsMenuOpen(false)}>JOIN VIP</a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* ============ HERO SECTION ============ */}
      <section ref={heroRef} className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image with Parallax */}
        <motion.div 
          className="absolute inset-0 z-0"
          style={{ scale: heroScale }}
        >
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `url('https://images.unsplash.com/photo-1548407260-da850faa41e3?w=1920&q=80')`,
              filter: 'brightness(0.3) contrast(1.1)'
            }}
          />
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-transparent to-[#050505]" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#050505]/80 via-transparent to-[#050505]/80" />
        </motion.div>

        {/* Hero Content */}
        <motion.div 
          className="relative z-10 text-center px-6 max-w-5xl mx-auto"
          style={{ opacity: heroOpacity }}
        >
          {/* Sacred Number */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="mb-8"
          >
            <span 
              className="text-[#D4AF37] text-6xl md:text-8xl font-bold tracking-tight"
              style={{ fontFamily: 'Cinzel Decorative, serif', textShadow: '0 0 60px rgba(212, 175, 55, 0.4)' }}
            >
              888
            </span>
          </motion.div>

          {/* Main Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-4xl md:text-6xl lg:text-7xl font-extrabold tracking-tighter leading-[0.9] mb-6"
            style={{ fontFamily: 'Syne, sans-serif' }}
          >
            DEDICATED TO <span className="text-[#D4AF37]">YHWH יהוה</span>
            <br />
            <span className="text-white/60">THE LIVING STONES</span>
            <br />
            & THE LAST
          </motion.h1>

          {/* Scripture */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="text-white/50 text-sm md:text-base max-w-2xl mx-auto mb-8 italic"
            style={{ fontFamily: 'Cinzel Decorative, serif' }}
          >
            "As you come to him, a living stone rejected by men, but chosen precious to YHWH יהוה."
            <span className="block mt-2 text-[#D4AF37]/70">— 1 Peter 2:4</span>
          </motion.p>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <a 
              href="#vip"
              className="group px-8 py-4 bg-white text-black font-semibold text-sm tracking-wide hover:bg-[#D4AF37] transition-all duration-300 flex items-center space-x-2"
              style={{ fontFamily: 'Manrope, sans-serif' }}
              data-testid="hero-cta-primary"
            >
              <span>ENTER THE CAVE</span>
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </a>
            <a 
              href="#about"
              className="px-8 py-4 border border-white/20 text-white font-medium text-sm tracking-wide hover:border-white/60 transition-all duration-300"
              style={{ fontFamily: 'Manrope, sans-serif' }}
              data-testid="hero-cta-secondary"
            >
              EXPLORE THE CULTURE
            </a>
          </motion.div>
        </motion.div>

        {/* Scroll Indicator */}
        <motion.div 
          className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <ChevronDown size={32} className="text-[#D4AF37]/60" />
        </motion.div>
      </section>

      {/* ============ BRAND STATEMENT ============ */}
      <section id="about" className="relative py-24 md:py-32 overflow-hidden">
        {/* Concrete Texture Background */}
        <div 
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1617200352408-c8df9bf6cd6f?w=1920&q=80')`,
            backgroundSize: 'cover',
            mixBlendMode: 'overlay'
          }}
        />
        
        <div className="relative z-10 max-w-6xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            {/* Overline */}
            <span 
              className="text-[#D4AF37] text-xs tracking-[0.3em] uppercase block mb-6"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              The Formula
            </span>
            
            {/* Main Statement */}
            <h2 
              className="text-3xl md:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.1] mb-8"
              style={{ fontFamily: 'Syne, sans-serif' }}
            >
              <span className="text-[#4B0082]">YAH SPIRIT</span>
              <span className="text-white/40 mx-4">+</span>
              <span className="text-white">STREETWEAR</span>
              <br />
              <span className="text-white/40">=</span>
              <span className="text-[#D4AF37] ml-4">INSPIYAH CULTURE</span>
            </h2>

            {/* Description */}
            <p 
              className="text-white/60 text-lg md:text-xl max-w-3xl mx-auto leading-relaxed"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              On 888 I open heaven's gate and introduce YAH SPIRIT + STREETWEAR = INSPIYAH CULTURE. 
              It's time to let Yah Spirit sear its word on Yah soul. Get ready to Drip with the 
              INSPIYAH CULTURE where every TEEE tells a story, and every hoodie is INSPIYAHED with purpose.
            </p>

            {/* Philosophy Banner */}
            <div className="mt-16 p-8 border border-white/10 bg-white/[0.02] backdrop-blur-sm">
              <p 
                className="text-white/80 text-lg md:text-xl tracking-wide leading-relaxed"
                style={{ fontFamily: 'Cinzel Decorative, serif' }}
              >
                "Let <span className="text-[#D4AF37]">Yah Spirit</span> Sear Its Word on <span className="text-[#D4AF37]">Yah Soul</span>.
                <br />
                <span className="text-white mt-2 block" style={{ fontFamily: 'Syne, sans-serif' }}>Drip to Sculpture the INSPIYAH Culture.</span>"
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ============ CAVE OF TREASURES - VIP SIGNUP ============ */}
      <section id="vip" className="relative py-24 md:py-32 overflow-hidden">
        {/* Light Beam Background */}
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=1920&q=80')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'brightness(0.15) sepia(0.3)'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-transparent to-[#050505]" />

        <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            {/* Cave Icon */}
            <div className="w-20 h-20 mx-auto mb-8 rounded-full border border-[#D4AF37]/30 flex items-center justify-center bg-[#D4AF37]/10">
              <span className="text-[#D4AF37] text-3xl" style={{ fontFamily: 'Cinzel Decorative, serif' }}>⛏</span>
            </div>

            <h2 
              className="text-3xl md:text-5xl font-extrabold tracking-tight mb-4"
              style={{ fontFamily: 'Syne, sans-serif' }}
            >
              ENTER THE <span className="text-[#D4AF37]">CAVE OF TREASURES</span>
            </h2>
            
            <p 
              className="text-white/60 text-base md:text-lg max-w-xl mx-auto mb-4"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Early access — Join The EPIIC HERO's N TEEE's VIP List Now!
            </p>
            
            <p 
              className="text-white/40 text-sm max-w-lg mx-auto mb-10"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              VIP subscribers get a free gift with purchase of our first collection of Graphic T-shirts — EPIIC Heros N Teee's ONYX.
            </p>

            {/* Email Form */}
            {!submitted ? (
              <form onSubmit={handleEmailSubmit} className="max-w-md mx-auto">
                <div className="relative group">
                  {/* Animated Border */}
                  <div className="absolute -inset-[2px] bg-gradient-to-r from-[#D4AF37] via-[#FF4500] to-[#D4AF37] rounded-sm opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-sm" />
                  <div className="absolute -inset-[1px] bg-gradient-to-r from-[#D4AF37] via-[#FF4500] to-[#D4AF37] rounded-sm opacity-0 group-focus-within:opacity-100 transition-opacity duration-300" />
                  
                  <div className="relative flex bg-[#0A0A0A] border border-white/10 rounded-sm overflow-hidden">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your email"
                      className="flex-1 px-6 py-4 bg-transparent text-white placeholder-white/30 focus:outline-none text-base"
                      style={{ fontFamily: 'Manrope, sans-serif' }}
                      data-testid="vip-email-input"
                      required
                    />
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="px-6 py-4 bg-[#D4AF37] text-black font-semibold hover:bg-white transition-colors disabled:opacity-50 flex items-center space-x-2"
                      style={{ fontFamily: 'Manrope, sans-serif' }}
                      data-testid="vip-submit-btn"
                    >
                      {isSubmitting ? (
                        <span className="animate-spin">⟳</span>
                      ) : (
                        <>
                          <span className="hidden sm:inline">ENTER</span>
                          <ArrowRight size={20} />
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </form>
            ) : (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="max-w-md mx-auto p-6 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-sm"
              >
                <p className="text-[#D4AF37] text-lg font-semibold" style={{ fontFamily: 'Syne, sans-serif' }}>
                  Welcome to the Cave! ✨
                </p>
                <p className="text-white/60 text-sm mt-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  You're now on the VIP list. Prepare for the DRIP.
                </p>
              </motion.div>
            )}
          </motion.div>
        </div>
      </section>

      {/* ============ VIDEO SHOWCASE ============ */}
      <section className="relative py-24 md:py-32 bg-[#030303]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <span 
              className="text-[#D4AF37] text-xs tracking-[0.3em] uppercase block mb-4"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              The Movement
            </span>
            <h2 
              className="text-3xl md:text-5xl font-extrabold tracking-tight"
              style={{ fontFamily: 'Syne, sans-serif' }}
            >
              JOIN THE <span className="text-[#D4AF37]">INSPIYAH</span> CULTURE
            </h2>
          </motion.div>

          {/* Video Grid - Cinema Mode */}
          <div className="grid md:grid-cols-2 gap-8 md:gap-12">
            {/* Video 1 */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="group relative"
            >
              <div className="relative aspect-video bg-[#0A0A0A] border border-white/5 overflow-hidden">
                {/* Replace with actual video embed */}
                <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-[#4B0082]/20 to-[#D4AF37]/10">
                  <img 
                    src="https://inspiyahculture.com/cdn/shop/files/INSPIYAH_CULTURE_3.png?v=1725317169&width=800"
                    alt="Join the INSPIYAH Culture"
                    className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity"
                  />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-20 h-20 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center border border-white/20 group-hover:scale-110 group-hover:bg-[#D4AF37] transition-all duration-300 cursor-pointer">
                      <Play size={32} className="text-white ml-1 group-hover:text-black" />
                    </div>
                  </div>
                </div>
              </div>
              <p 
                className="mt-4 text-white/40 text-xs tracking-[0.2em] uppercase text-center"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                Embrace Your Uniqueness — Unleash Your Inner Hero
              </p>
            </motion.div>

            {/* Video 2 */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="group relative"
            >
              <div className="relative aspect-video bg-[#0A0A0A] border border-white/5 overflow-hidden">
                {/* Replace with actual video embed */}
                <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-[#FF4500]/20 to-[#D4AF37]/10">
                  <img 
                    src="https://inspiyahculture.com/cdn/shop/files/INSPIYAH_CULTURE_4.png?v=1725317805&width=800"
                    alt="Let's INSPIYAH Truth"
                    className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity"
                  />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-20 h-20 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center border border-white/20 group-hover:scale-110 group-hover:bg-[#D4AF37] transition-all duration-300 cursor-pointer">
                      <Play size={32} className="text-white ml-1 group-hover:text-black" />
                    </div>
                  </div>
                </div>
              </div>
              <p 
                className="mt-4 text-white/40 text-xs tracking-[0.2em] uppercase text-center"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                Let's INSPIYAH Truth
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ============ DRIP SECTION - PRODUCT TEASER ============ */}
      <section id="collection" className="relative py-24 md:py-32 overflow-hidden">
        {/* Fire Background */}
        <div 
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1475924156734-496f6cac6ec1?w=1920&q=80')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/95 to-[#050505]" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="grid lg:grid-cols-2 gap-12 items-center"
          >
            {/* Text Content */}
            <div>
              <span 
                className="text-[#FF4500] text-xs tracking-[0.3em] uppercase block mb-4"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                Coming Soon
              </span>
              
              <h2 
                className="text-4xl md:text-6xl font-extrabold tracking-tight leading-[0.95] mb-6"
                style={{ fontFamily: 'Syne, sans-serif' }}
              >
                DRIP TO <span className="text-[#D4AF37]">SCULPTURE</span>
                <br />
                THE INSPIYAH
                <br />
                <span className="text-white/40">CULTURE</span>
              </h2>

              <p 
                className="text-white/60 text-base md:text-lg leading-relaxed mb-8"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                For the true outcasts, living stones, sons & daughters of YHWH יהוה, chosen ones, 
                generational curse breakers, the stolen JEW-ELS, angels in training, indigenous 
                wisdom keepers, the wild women, black sheep, indigo children, and most importantly 
                the YAH lovers — we are offering an exclusive opportunity.
              </p>

              <div className="flex flex-wrap gap-3 mb-8">
                {['EPIIC HEROES', 'LIVING STONES', 'CHOSEN ONES', 'PHOENIX RISING'].map((tag) => (
                  <span 
                    key={tag}
                    className="px-4 py-2 bg-white/5 border border-white/10 text-white/60 text-xs tracking-wider"
                    style={{ fontFamily: 'Manrope, sans-serif' }}
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <a 
                href="#vip"
                className="inline-flex items-center space-x-3 px-8 py-4 bg-gradient-to-r from-[#D4AF37] to-[#FF4500] text-black font-semibold hover:opacity-90 transition-opacity"
                style={{ fontFamily: 'Manrope, sans-serif' }}
                data-testid="collection-cta"
              >
                <ShoppingBag size={20} />
                <span>GET EARLY ACCESS</span>
              </a>
            </div>

            {/* Product Image */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="relative aspect-square bg-[#0A0A0A] border border-white/5 overflow-hidden group">
                <img 
                  src="https://inspiyahculture.com/cdn/shop/files/INSPIYAH_CULTURE_6.png?v=1725394500&width=800"
                  alt="EPIIC Hero's N Teee's Collection"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                {/* Overlay Badge */}
                <div className="absolute top-6 right-6 px-4 py-2 bg-[#D4AF37] text-black text-xs font-bold tracking-wider">
                  ONYX COLLECTION
                </div>
              </div>

              {/* Collection Title */}
              <div className="mt-6 text-center">
                <h3 
                  className="text-2xl font-bold text-white/90"
                  style={{ fontFamily: 'Syne, sans-serif' }}
                >
                  EPIIC HERO's N TEEE's
                </h3>
                <p 
                  className="text-white/40 text-sm mt-2"
                  style={{ fontFamily: 'Manrope, sans-serif' }}
                >
                  Graphic T-shirt Collection — ONYX
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ============ BLOG SECTION ============ */}
      <section id="blog" className="relative py-24 md:py-32 bg-[#030303]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <span 
              className="text-[#D4AF37] text-xs tracking-[0.3em] uppercase block mb-4"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Knowledge
            </span>
            <h2 
              className="text-3xl md:text-5xl font-extrabold tracking-tight"
              style={{ fontFamily: 'Syne, sans-serif' }}
            >
              MIND <span className="text-[#D4AF37]">UNPLUGGED</span>
            </h2>
            <p 
              className="text-white/40 text-base mt-4 max-w-lg mx-auto"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              From INSPIYAH to Blog — Wisdom for the awakened soul
            </p>
          </motion.div>

          {/* Blog Card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-2xl mx-auto"
          >
            <a 
              href="https://inspiyahculture.com/blogs/news/why-inspiyah-what-is-the-epiic-hero"
              target="_blank"
              rel="noopener noreferrer"
              className="group block bg-[#0A0A0A] border border-white/5 overflow-hidden hover:border-[#D4AF37]/30 transition-all duration-300"
              data-testid="blog-card-link"
            >
              <div className="aspect-[16/9] overflow-hidden">
                <img 
                  src="https://inspiyahculture.com/cdn/shop/articles/aeeb5db0488872292e83ab697f09e931_1_dece5e14-f917-4061-b728-11c80c71fbec.webp?v=1723517932&width=800"
                  alt="Why INSPIYAH & What is the EPIIC Hero?"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
              </div>
              <div className="p-8">
                <span 
                  className="text-[#D4AF37]/60 text-xs tracking-wider uppercase"
                  style={{ fontFamily: 'Manrope, sans-serif' }}
                >
                  September 3, 2024
                </span>
                <h3 
                  className="text-xl md:text-2xl font-bold text-white mt-3 group-hover:text-[#D4AF37] transition-colors"
                  style={{ fontFamily: 'Syne, sans-serif' }}
                >
                  Why INSPIYAH & What is the EPIIC Hero?
                </h3>
                <p 
                  className="text-white/50 text-sm mt-4 leading-relaxed"
                  style={{ fontFamily: 'Manrope, sans-serif' }}
                >
                  The INSPIYAH CULTURE is born out of the EPIIC hero's journey as a brand of faith-based 
                  street wear fashion. It's time to let YAH spirit sear its word on YAH soul...
                </p>
                <div className="flex items-center space-x-2 mt-6 text-white/40 group-hover:text-[#D4AF37] transition-colors">
                  <span className="text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>Read More</span>
                  <ExternalLink size={14} />
                </div>
              </div>
            </a>
          </motion.div>
        </div>
      </section>

      {/* ============ FOOTER ============ */}
      <footer className="relative py-24 md:py-32 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          {/* Giant Brand Name */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1 }}
            className="text-center mb-16"
          >
            <h2 
              className="text-[15vw] font-extrabold tracking-tighter leading-none text-white/[0.03]"
              style={{ fontFamily: 'Syne, sans-serif' }}
            >
              INSPIYAH
            </h2>
          </motion.div>

          {/* Footer Content */}
          <div className="grid md:grid-cols-3 gap-12 text-center md:text-left">
            {/* Brand */}
            <div>
              <h3 
                className="text-xl font-bold mb-4"
                style={{ fontFamily: 'Syne, sans-serif' }}
              >
                INSPIYAH CULTURE
              </h3>
              <p 
                className="text-white/40 text-sm leading-relaxed"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                YAH SPIRIT + STREETWEAR
                <br />
                Faith-based fashion for the awakened.
              </p>
            </div>

            {/* Links */}
            <div>
              <h4 
                className="text-xs tracking-[0.2em] uppercase text-white/60 mb-4"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                Quick Links
              </h4>
              <ul className="space-y-3">
                <li><a href="#about" className="text-white/40 hover:text-white text-sm transition-colors" style={{ fontFamily: 'Manrope, sans-serif' }}>About</a></li>
                <li><a href="#collection" className="text-white/40 hover:text-white text-sm transition-colors" style={{ fontFamily: 'Manrope, sans-serif' }}>Collection</a></li>
                <li><a href="#blog" className="text-white/40 hover:text-white text-sm transition-colors" style={{ fontFamily: 'Manrope, sans-serif' }}>Blog</a></li>
                <li><a href="#vip" className="text-white/40 hover:text-white text-sm transition-colors" style={{ fontFamily: 'Manrope, sans-serif' }}>VIP Access</a></li>
              </ul>
            </div>

            {/* Social */}
            <div>
              <h4 
                className="text-xs tracking-[0.2em] uppercase text-white/60 mb-4"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                Connect
              </h4>
              <div className="flex items-center justify-center md:justify-start space-x-4">
                <a href="#" className="w-10 h-10 border border-white/10 flex items-center justify-center hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors" data-testid="social-instagram">
                  <Instagram size={18} />
                </a>
                <a href="#" className="w-10 h-10 border border-white/10 flex items-center justify-center hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors" data-testid="social-youtube">
                  <Youtube size={18} />
                </a>
                <a href="#" className="w-10 h-10 border border-white/10 flex items-center justify-center hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors" data-testid="social-mail">
                  <Mail size={18} />
                </a>
              </div>
            </div>
          </div>

          {/* Scripture & Copyright */}
          <div className="mt-16 pt-8 border-t border-white/5 text-center">
            <p 
              className="text-white/30 text-xs italic mb-4"
              style={{ fontFamily: 'Cinzel Decorative, serif' }}
            >
              "I WILL BOAST IN YHWH יהוה, THE MEEK WILL HEAR & REJOICE" — Psalm 34:2
            </p>
            <p 
              className="text-white/20 text-xs"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              © {new Date().getFullYear()} INSPIYAH CULTURE. All rights reserved.
              <br />
              <span className="text-[#D4AF37]/40">Let Yah Spirit Sear Its Word on Yah Soul. Drip to Sculpture the INSPIYAH Culture.™</span>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default InspiyahCulture;
